import React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { CheckoutFeedback } from "@/services/interfaces/IFeedbackService";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Star } from "lucide-react";
import { GuestNameWithFlag } from "@/components/pages/reservations/guests/components/GuestNameWithFlag";
import { cn } from "@/lib/utils";
import { ChatSection } from "./ChatSection";

interface ReservationFeedbackModalProps {
    feedback: CheckoutFeedback | null;
    isOpen: boolean;
    onClose: () => void;
}

export const ReservationFeedbackModal: React.FC<ReservationFeedbackModalProps> = ({
    feedback,
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();

    if (!feedback) return null;

    const guest = feedback.guest;
    const booking = feedback.booking;

    const guestName =
        `${guest?.profile.firstName || ""} ${guest?.profile.lastName || ""}`.trim();
    const countryCode = guest?.profile.language || "";
    const roomInfo = `${t("feedbacks.modal.room", "room")} ${booking?.room?.number || ""} ${booking?.room?.category?.name || ""}`;

    const arrivalDate = booking?.arrivalDate
        ? new Date(booking.arrivalDate)
        : null;
    const departureDate = booking?.departureDate
        ? new Date(booking.departureDate)
        : null;
    const feedbackDate = feedback.createdAt ? new Date(feedback.createdAt) : null;

    const renderStars = (rating: number) => {
        return (
            <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                        height={14}
                        width={14}
                        key={i}
                        className={cn(
                            "h-5 w-5",
                            i < rating
                                ? "fill-black text-black"
                                : "fill-gray-200 text-gray-200",
                        )}
                    />
                ))}
            </div>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] gap-0">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold text-center">
                        {t("feedbacks.modal.reservationTitle", "Reservation Feedback")}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                    {/* Guest Info */}
                    <div className="flex items-center justify-center gap-2">
                        <GuestNameWithFlag
                            className="text-gray-5"
                            name={guestName}
                            countryCode={countryCode}
                        />
                        <span className="text-gray-5">, {roomInfo}</span>
                    </div>

                    {/* Reservation Number */}
                    <div className="flex gap-4 items-center">
                        <span className="text-gray-5 text-sm">
                            {t("feedbacks.modal.reservationNumber", "Reservation number")}
                        </span>
                        <span className="font-medium">
                            {booking?.bookingNumber || "N/A"}
                        </span>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-8">
                        <div className="flex gap-2 items-center">
                            <span className="text-gray-5 text-sm">
                                {t("feedbacks.modal.arrival", "Guest arrival")}
                            </span>
                            <div>
                                <div className="font-medium">
                                    {arrivalDate ? format(arrivalDate, "dd.MM.yyyy") : "N/A"}
                                </div>
                                <div className="text-gray-5">
                                    {arrivalDate ? format(arrivalDate, "HH:mm") : ""}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 items-center">
                            <span className="text-gray-5 text-sm">
                                {t("feedbacks.modal.departure", "Guest departure")}
                            </span>
                            <div>
                                <div className="font-medium">
                                    {departureDate ? format(departureDate, "dd.MM.yyyy") : "N/A"}
                                </div>
                                <div className="text-gray-5">
                                    {departureDate ? format(departureDate, "HH:mm") : ""}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Feedback */}
                    <div className="flex flex-col items-start gap-4">
                        <span className="text-gray-5 min-w-[100px] text-sm">
                            {t("feedbacks.modal.feedback", "Feedback")}
                        </span>
                        <span className="font-medium">
                            &ldquo;{feedback.comment ||
                                t("feedbacks.modal.noComments", "No comments.")}&rdquo;
                        </span>
                    </div>

                    {/* Evaluations */}
                    <div className="flex items-center justify-between">
                        <span className="text-gray-5 text-sm">
                            {t("feedbacks.modal.evaluations", "Evaluations")}
                        </span>
                        {renderStars(feedback.rating)}
                    </div>

                    {/* Details Section */}
                    {feedback.categoriesRating && Object.keys(feedback.categoriesRating).length > 0 && (
                        <div className="space-y-4">
                            <span className="text-gray-5 text-sm font-semibold">
                                {t("feedbacks.modal.details", "Details")}
                            </span>
                            <div className="space-y-2">
                                {Object.entries(feedback.categoriesRating).map(([category, rating]) => (
                                    <div key={category} className="flex items-center justify-between">
                                        <span className="text-gray-700 text-sm capitalize">{category}</span>
                                        {renderStars(parseInt(rating))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Date */}
                    <div className="flex items-center justify-between">
                        <span className="text-gray-5 text-sm">
                            {t("feedbacks.modal.date", "Date")}
                        </span>
                        <div className="flex gap-2 font-medium">
                            <span>
                                {feedbackDate ? format(feedbackDate, "dd.MM.yyyy") : "N/A"}
                            </span>
                            <span className="text-gray-5">
                                {feedbackDate ? format(feedbackDate, "HH:mm") : ""}
                            </span>
                        </div>
                    </div>

                    {/* Chat Section */}
                    <ChatSection bookingId={feedback.bookingId} guestId={feedback.guestId} />
                </div>
            </DialogContent>
        </Dialog>
    );
};
